// Multi-select state for an email surface (bulk actions). The math lives in
// lib/email/selection.ts; this hook owns the React state and the shift-click anchor.
//
// Lifecycle mirrors useStickyPill: the selection is scoped to an epoch = (pill, list-scope,
// mailbox…) chosen by the caller, and is emptied DURING THE RENDER that observes an epoch
// change. Acting on rows the user can no longer see is the failure mode being designed out
// — a bulk write is destructive enough that "selected but off-screen" must never happen.
//
// Selection is deliberately NOT cleared by a search keystroke or the 60s poll: those change
// which rows are visible without changing what the user meant to select, and `visibleSelected`
// already prunes anything off-screen at the moment an action fires.

import { useCallback, useMemo, useRef, useState } from "react";
import { pruneToVisible, rangeBetween } from "@/lib/email/selection";

const EMPTY_SELECTION: ReadonlySet<string> = new Set<string>();

export interface EmailSelection {
  ids: ReadonlySet<string>;
  count: number;
  isSelected: (id: string) => boolean;
  /** Plain click toggles one row; shift-click ADDs the whole range from the anchor
   *  (Outlook semantics — a shift-click never deselects). The anchor moves in both modes. */
  toggle: (id: string, shiftKey: boolean) => void;
  clear: () => void;
  /** count > 0 — surfaces use this to force every row's checkbox visible. */
  active: boolean;
  /** The ids to actually act on: selection ∩ visible, in visible order. */
  visibleSelected: string[];
}

export function useEmailSelection(
  orderedVisibleIds: readonly string[],
  epochKey: string,
): EmailSelection {
  const [ids, setIds] = useState<ReadonlySet<string>>(EMPTY_SELECTION);
  const [seenEpoch, setSeenEpoch] = useState(epochKey);
  const anchorRef = useRef<string | null>(null);

  // Keep the current visible order reachable from `toggle` without making the callback
  // depend on it — the order array is rebuilt on most renders, and a new `toggle` identity
  // every render would churn every row.
  const orderRef = useRef(orderedVisibleIds);
  orderRef.current = orderedVisibleIds;

  // Render-phase epoch reset (React's "adjust state when a prop changes"), NOT an effect —
  // an effect-based reset would render one frame in which rows from the previous epoch are
  // still selected and the bulk bar still shows their count.
  //
  // This is the useState form of the same reset in useStickyPill (whose comment recommends
  // exactly this form): setState-during-render is rollback-safe, so the selection survives a
  // discarded render correctly. The anchor is a ref and carries useStickyPill's precondition
  // — mutating a ref during render is not rollback-safe if React discards the render. Benign
  // here: a lost or stale anchor only affects the NEXT shift-click, and rangeBetween already
  // degrades to a single-row selection when the anchor is not in the visible list.
  let current = ids;
  if (seenEpoch !== epochKey) {
    setSeenEpoch(epochKey);
    setIds(EMPTY_SELECTION);
    anchorRef.current = null;
    current = EMPTY_SELECTION;
  }

  const toggle = useCallback((id: string, shiftKey: boolean) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (shiftKey) {
        for (const r of rangeBetween(orderRef.current, anchorRef.current, id)) next.add(r);
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    anchorRef.current = id;
  }, []);

  const clear = useCallback(() => {
    anchorRef.current = null;
    setIds((prev) => (prev.size === 0 ? prev : EMPTY_SELECTION));
  }, []);

  const isSelected = useCallback((id: string) => current.has(id), [current]);

  const visibleSelected = useMemo(
    () => pruneToVisible(current, orderedVisibleIds),
    [current, orderedVisibleIds],
  );

  return {
    ids: current,
    count: current.size,
    isSelected,
    toggle,
    clear,
    active: current.size > 0,
    visibleSelected,
  };
}
